import { useState, useEffect, useRef } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Badge } from 'foundations-components/transitional/components/Badge';
import { Flex } from 'foundations-components/components/Flex';
import { TruncateString } from 'foundations-components/transitional/components/TruncateString';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { useCardContext } from '../../../context/CardContext';
import AddAssociationLabelButton from './AddAssociationLabelButton';
import useElementWidthObserver from '../../../hooks/useElementWidthObserver';
import I18n from 'I18n';
import { View } from 'foundations-components/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const AVG_LABEL_WIDTH = 80;
const MORE_LABELS_BADGE_WIDTH = 80;
const LABEL_GAP = 8;
const WIDTH_PER_CHARACTER = 6;
const LABEL_OFFSET = 24;
const MIN_VISIBLE_LABELS = 1;
function calculateVisibleLabelCount(namedLabels, containerWidth) {
  let totalWidth = 0;
  let visibleCount = 0;
  const availableWidth = containerWidth - (namedLabels.length > Math.max(MIN_VISIBLE_LABELS, Math.floor(containerWidth / AVG_LABEL_WIDTH)) ? MORE_LABELS_BADGE_WIDTH : 0);
  for (let i = 0; i < namedLabels.length; i++) {
    const estimatedLabelWidth = (i > 0 ? LABEL_GAP : 0) + Math.min(AVG_LABEL_WIDTH,
    // Width Estimation Formula approved by UI/UX
    namedLabels[i].length * WIDTH_PER_CHARACTER + LABEL_OFFSET);
    if (totalWidth + estimatedLabelWidth <= availableWidth || visibleCount < MIN_VISIBLE_LABELS) {
      totalWidth += estimatedLabelWidth;
      visibleCount++;
    } else {
      break;
    }
  }
  return Math.max(MIN_VISIBLE_LABELS, Math.min(visibleCount, namedLabels.length));
}
const createMoreLabelsBadge = hiddenLabels => {
  const hiddenLabelsText = I18n.formatList(hiddenLabels);
  const ariaLabel = `${I18n.text('cardAssociatedObjects.chicklet.AssociationChicklet.moreLabels', {
    count: hiddenLabels.length
  })}: ${hiddenLabelsText}`;
  return /*#__PURE__*/_jsx(Badge, {
    use: "koala",
    textWrap: "nowrap",
    "aria-label": ariaLabel,
    cursor: "help",
    children: /*#__PURE__*/_jsx(Tooltip, {
      title: hiddenLabelsText,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "cardAssociatedObjects.chicklet.AssociationChicklet.moreLabels",
        options: {
          count: hiddenLabels.length
        }
      })
    })
  });
};
function AssociationChicletAssociationLabels({
  labels,
  association
}) {
  const {
    toObjectTypeId
  } = useCardContext();
  const {
    namedLabels
  } = labels;
  const containerRef = useRef(null);
  const containerWidth = useElementWidthObserver(containerRef);
  const [visibleLabels, setVisibleLabels] = useState([]);
  const [hiddenLabels, setHiddenLabels] = useState([]);
  useEffect(() => {
    if (containerWidth === 0 || namedLabels.length === 0) {
      setVisibleLabels(namedLabels);
      setHiddenLabels([]);
      return;
    }
    const visibleCount = calculateVisibleLabelCount(namedLabels, containerWidth);
    setVisibleLabels(namedLabels.slice(0, visibleCount));
    setHiddenLabels(namedLabels.slice(visibleCount));
  }, [containerWidth, namedLabels]);
  if (namedLabels.length === 0) {
    return /*#__PURE__*/_jsx(View, {
      as: "div",
      marginBlockStart: "200",
      children: /*#__PURE__*/_jsx(AddAssociationLabelButton, {
        association: association
      })
    });
  }
  return /*#__PURE__*/_jsx(View, {
    as: "div",
    marginBlockStart: "200",
    ref: containerRef,
    children: /*#__PURE__*/_jsxs(Flex, {
      gap: "200",
      children: [visibleLabels.map(label => /*#__PURE__*/_jsx(Badge, {
        use: "koala",
        "data-test-id": `${toObjectTypeId}-${label}`,
        tabIndex: 0,
        children: /*#__PURE__*/_jsx(TruncateString, {
          matchContentWidth: true,
          children: label
        })
      }, label)), hiddenLabels.length > 0 && createMoreLabelsBadge(hiddenLabels)]
    })
  });
}
export default AssociationChicletAssociationLabels;